import React, { useEffect, useState } from 'react';
import { AppState, FinalResult, ThemeMode, TransportMode } from '../types';
import MapComponent from './MapComponent';
import Sounds from '../utils/sounds';
import { trackRestaurantClicked } from '../utils/analytics';
import { getCurrentMinutesAtLocation, prefetchTimezone, fetchTimezoneOffset } from '../lib/timezoneService';

// Cache for timezone offsets (by place_id)
const timezoneOffsetCache = new Map<string, number>();

/**
 * Parse time string like "11:00 am", "2:30 pm", "14:00" into minutes since midnight
 */
const parseTimeToMinutes = (timeStr: string): number | null => {
  const cleaned = timeStr.trim().toLowerCase();
  
  // Handle 24-hour format (e.g., "14:00")
  const match24 = cleaned.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    const hours = parseInt(match24[1], 10);
    const minutes = parseInt(match24[2], 10);
    return hours * 60 + minutes;
  }
  
  // Handle 12-hour format (e.g., "2:30 pm", "11:00 am")
  const match12 = cleaned.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
  if (match12) {
    let hours = parseInt(match12[1], 10);
    const minutes = parseInt(match12[2], 10);
    const period = match12[3];
    
    if (period === 'pm' && hours !== 12) hours += 12;
    if (period === 'am' && hours === 12) hours = 0;
    
    return hours * 60 + minutes;
  }
  
  return null;
};

/**
 * Check if current time falls within a single time range
 * Returns true if open, false if closed, null if can't parse
 * 
 * @param rangeStr Time range string like "8:00 am – 4:00 pm"
 * @param lat Latitude of the restaurant
 * @param lng Longitude of the restaurant
 * @param timezoneOffset Cached timezone offset in seconds (from Google Time Zone API)
 */
const isWithinTimeRange = (rangeStr: string, lat?: number, lng?: number, timezoneOffset?: number | null): boolean | null => {
  // Split by common separators: "–", "-", "to"
  const parts = rangeStr.split(/\s*[–\-]\s*|\s+to\s+/i);
  if (parts.length !== 2) return null;
  
  const openTime = parseTimeToMinutes(parts[0]);
  const closeTime = parseTimeToMinutes(parts[1]);
  
  if (openTime === null || closeTime === null) return null;
  
  // Get current time at the restaurant's location (uses Google Time Zone API when available)
  const currentMinutes = (lat !== undefined && lng !== undefined) 
    ? getCurrentMinutesAtLocation(lat, lng, timezoneOffset)
    : new Date().getHours() * 60 + new Date().getMinutes();
  
  // Handle overnight hours (e.g., 6pm - 2am)
  if (closeTime < openTime) {
    return currentMinutes >= openTime || currentMinutes < closeTime;
  }
  
  return currentMinutes >= openTime && currentMinutes < closeTime;
};

/**
 * Extract opening time from a time range string like "11:00 am – 10:00 pm"
 * Returns the formatted opening time (e.g., "11:00 AM") or null if can't parse
 */
const extractOpeningTime = (rangeStr: string): string | null => {
  const parts = rangeStr.split(/\s*[–\-]\s*|\s+to\s+/i);
  if (parts.length !== 2) return null;
  
  const openTimeStr = parts[0].trim();
  // Validate it's a parseable time
  if (parseTimeToMinutes(openTimeStr) === null) return null;
  
  // Format nicely (capitalize AM/PM)
  return openTimeStr.toUpperCase().replace(/\s+/g, ' ');
};

/**
 * Check if a place is explicitly closed all day (no opening hours at all)
 */
const isClosedAllDay = (todaysHours: string | null | undefined): boolean => {
  if (!todaysHours) return false; // Unknown, not explicitly closed
  const cleaned = todaysHours.trim().toLowerCase();
  return cleaned === 'closed';
};

/**
 * Get the next opening time from today's hours string
 * Returns formatted time like "11:00 AM" or null if closed all day or can't determine
 * 
 * @param todaysHours Today's hours string
 * @param lat Latitude of the restaurant
 * @param lng Longitude of the restaurant
 * @param timezoneOffset Cached timezone offset in seconds
 */
const getNextOpeningTime = (todaysHours: string | null | undefined, lat?: number, lng?: number, timezoneOffset?: number | null): string | null => {
  if (!todaysHours) return null;
  
  const cleaned = todaysHours.trim().toLowerCase();
  if (cleaned === 'closed') return null; // Closed all day
  if (cleaned === 'open 24 hours') return null; // Always open
  
  // Get current time at the restaurant's location
  const currentMinutes = (lat !== undefined && lng !== undefined)
    ? getCurrentMinutesAtLocation(lat, lng, timezoneOffset)
    : new Date().getHours() * 60 + new Date().getMinutes();
  
  // Split by comma to handle multiple time ranges (e.g., lunch and dinner)
  const timeRanges = todaysHours.split(/\s*,\s*/);
  
  // Find the next opening time that's after current time
  for (const range of timeRanges) {
    const parts = range.split(/\s*[–\-]\s*|\s+to\s+/i);
    if (parts.length !== 2) continue;
    
    const openTime = parseTimeToMinutes(parts[0]);
    if (openTime === null) continue;
    
    // If this opening time is in the future, return it
    if (openTime > currentMinutes) {
      return extractOpeningTime(range);
    }
  }
  
  // No future opening time found today (closed for rest of day)
  return null;
};

/**
 * Check if a place is currently open based on today's hours string
 * Returns: { isOpen: boolean | null, nextOpenTime: string | null }
 * 
 * @param todaysHours Today's hours string
 * @param lat Latitude of the restaurant
 * @param lng Longitude of the restaurant
 * @param timezoneOffset Cached timezone offset in seconds (from Google Time Zone API)
 */
const getOpenStatus = (todaysHours: string | null | undefined, lat?: number, lng?: number, timezoneOffset?: number | null): { isOpen: boolean | null; nextOpenTime: string | null } => {
  if (!todaysHours) return { isOpen: null, nextOpenTime: null };
  
  const cleaned = todaysHours.trim().toLowerCase();
  if (cleaned === 'closed') return { isOpen: false, nextOpenTime: null };
  if (cleaned === 'open 24 hours') return { isOpen: true, nextOpenTime: null };
  
  // Split by comma to handle multiple time ranges (e.g., lunch and dinner)
  const timeRanges = todaysHours.split(/\s*,\s*/);
  
  for (const range of timeRanges) {
    // Pass coordinates and timezone offset for accurate time comparison
    const isOpen = isWithinTimeRange(range.trim(), lat, lng, timezoneOffset);
    if (isOpen === true) {
      return { isOpen: true, nextOpenTime: null };
    }
  }
  
  // Not currently open - find next opening time (timezone-aware)
  const nextOpenTime = getNextOpeningTime(todaysHours, lat, lng, timezoneOffset);
  
  // If we parsed at least one range and none matched, it's closed
  const parsedAny = timeRanges.some(range => isWithinTimeRange(range.trim(), lat, lng, timezoneOffset) !== null);
  return { 
    isOpen: parsedAny ? false : null, 
    nextOpenTime 
  };
};

interface ResultsViewProps {
  appState: AppState;
  results: FinalResult[];
  userLat: number | null;
  userLng: number | null;
  onReset: () => void;
  theme: ThemeMode;
  transportMode: TransportMode;
  userAddress?: string;
}

/**
 * Generate a funny, short title based on the results
 */
const generateFunnyTitle = (results: FinalResult[]): string => {
  const titles: string[] = [];
  
  // Based on number of results
  if (results.length === 1) {
    titles.push("THE CHOSEN ONE", "FATE HAS SPOKEN", "YOUR DESTINY");
  } else if (results.length >= 5) {
    titles.push("FEAST MODE", "OPTION OVERLOAD", "DECISION PARALYSIS");
  }
  
  // Based on top result characteristics
  const topResult = results[0];
  if (topResult) {
    if (topResult.rating && topResult.rating >= 4.5) {
      titles.push("CERTIFIED BANGERS", "ELITE SELECTIONS", "PEAK CUISINE");
    }
    if (topResult.price_level === 1) {
      titles.push("BUDGET WINNERS", "CHEAP EATS SECURED", "WALLET FRIENDLY");
    }
    if (topResult.price_level && topResult.price_level >= 3) {
      titles.push("TREAT YOURSELF", "FANCY HOUR", "BOUGIE BITES");
    }
    if (topResult.walking_time_text?.includes("1 min") || topResult.walking_time_text?.includes("2 min")) {
      titles.push("LAZY MODE ENGAGED", "ALMOST THERE", "AROUND THE CORNER");
    }
    if (topResult.is_new_opening) {
      titles.push("FRESH FINDS", "NEW KID ALERT", "VIRGIN TERRITORY");
    }
  }
  
  // Fallback options
  titles.push(
    "CALCULATED OUTPUT",
    "LUNCH VERDICT",
    "HUNGER SOLUTIONS",
    "FEED THE BEAST",
    "SUSTENANCE ACQUIRED",
    "MISSION RESULTS",
    "OPERATION: LUNCH",
    "CHOW DETECTED",
    "FOOD RADAR RESULTS",
    "OPTIMAL FUEL SOURCES"
  );
  
  // Pick a random title
  return titles[Math.floor(Math.random() * titles.length)];
};

const ResultsView: React.FC<ResultsViewProps> = ({
  appState,
  results,
  userLat,
  userLng,
  onReset,
  theme,
  transportMode,
  userAddress = ''
}) => {
  const isDark = theme === ThemeMode.DARK;
  
  // Get travel mode label for display
  const travelModeLabel = transportMode === TransportMode.DRIVE ? 'DRIVE' 
    : transportMode === TransportMode.TRANSIT ? 'TRANSIT' : 'WALK';
  
  // Generate title once per results set using useMemo
  const funnyTitle = React.useMemo(() => generateFunnyTitle(results), [results]);

  // State to track fetched timezone offsets (triggers re-render when loaded)
  const [timezoneOffsets, setTimezoneOffsets] = useState<Map<string, number>>(new Map());

  // Fetch timezone offsets for all results when they load
  useEffect(() => {
    const fetchTimezones = async () => {
      const newOffsets = new Map<string, number>();
      
      for (const place of results) {
        // Get lat/lng from place geometry
        const lat = place.geometry?.location 
          ? (typeof place.geometry.location.lat === 'function' 
              ? place.geometry.location.lat() 
              : place.geometry.location.lat)
          : undefined;
        const lng = place.geometry?.location 
          ? (typeof place.geometry.location.lng === 'function' 
              ? place.geometry.location.lng() 
              : place.geometry.location.lng)
          : undefined;
        
        if (lat !== undefined && lng !== undefined) {
          // Check if we already have it cached
          const cached = timezoneOffsetCache.get(place.place_id);
          if (cached !== undefined) {
            newOffsets.set(place.place_id, cached);
          } else {
            // Prefetch in background (don't await all at once)
            prefetchTimezone(lat, lng);
            
            // Fetch and cache
            const offset = await fetchTimezoneOffset(lat, lng);
            if (offset !== null) {
              timezoneOffsetCache.set(place.place_id, offset);
              newOffsets.set(place.place_id, offset);
            }
          }
        }
      }
      
      if (newOffsets.size > 0) {
        setTimezoneOffsets(new Map(newOffsets));
      }
    };
    
    if (results.length > 0) {
      fetchTimezones();
    }
  }, [results]);

  const handleReset = () => {
    Sounds.firmClick();
    onReset();
  };

  // Generate Google Maps URL for a place (works on desktop, mobile browsers, AND native Maps app)
  const getPlaceUrl = (name: string, vicinity: string | undefined, placeId: string) => {
    const query = encodeURIComponent(`${name} ${vicinity || ''}`.trim());
    return `https://www.google.com/maps/search/?api=1&query=${query}&query_place_id=${placeId}`;
  };

  if (appState !== AppState.RESULTS) return null;

  return (
    <div className={`flex-1 p-3 sm:p-4 sm:flex sm:items-center sm:justify-center transition-colors duration-300 ${isDark ? 'bg-dark-bg' : 'bg-braun-bg'}`}>
      {/* Main Chassis - full height page scroll on mobile, fixed height inline scroll on desktop */}
      <div className={`w-full sm:h-[calc(100vh-2rem)] sm:max-h-[800px] max-w-7xl mx-auto border shadow-braun-deep flex flex-col transition-colors duration-300 ${isDark ? 'bg-dark-bg border-dark-border shadow-dark-deep' : 'bg-braun-bg border-braun-border shadow-braun-deep'}`}>
        
        {/* Header */}
        <div className={`px-4 py-4 sm:p-6 flex justify-between items-center sm:items-end border-b flex-shrink-0 transition-colors duration-300 ${isDark ? 'border-dark-border' : 'border-braun-border'}`}>
          <div>
            <h1 className={`font-sans font-bold text-base sm:text-xl tracking-tight leading-none ${isDark ? 'text-dark-text' : 'text-braun-dark'}`}>{funnyTitle}</h1>
          </div>
          <button 
            onClick={handleReset}
            className={`font-mono text-[10px] sm:text-[10px] font-bold uppercase tracking-widest transition-colors focus:outline-none focus:ring-2 focus:ring-white/30 px-2 py-2 sm:py-1 ${isDark ? 'text-[#999] hover:text-braun-orange' : 'text-braun-text-muted hover:text-braun-orange'}`}
          >
            <span className="hidden sm:inline">[ RESET SYSTEM ]</span>
            <span className="sm:hidden">RESET</span>
          </button>
        </div>

        {/* Split View - takes remaining height */}
        <div className={`flex-1 sm:min-h-0 flex flex-col lg:flex-row transition-colors duration-300 ${isDark ? 'bg-[#151515]' : 'bg-[#F9F9F7]'}`}>
          
          {/* List Column - scrollable on desktop only */}
          <div className="flex-1 sm:overflow-y-auto sm:min-h-0">
            {results.map((place, idx) => {
              // Find today's hours by matching the day name in the weekday_text strings
              // IMPORTANT: Google Places API ALWAYS returns English day names, regardless of locale
              // So we must use English day names for the lookup, not the browser's locale
              const englishDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              const todayName = englishDays[new Date().getDay()];
              const todaysHoursRaw = place.opening_hours?.weekday_text?.find(
                text => text.toLowerCase().startsWith(todayName.toLowerCase())
              );
              const todaysHours = todaysHoursRaw ? todaysHoursRaw.substring(todaysHoursRaw.indexOf(':') + 2) : null;
              
              // Get restaurant's coordinates for timezone-aware time calculations
              const restaurantLat = place.geometry?.location 
                ? (typeof place.geometry.location.lat === 'function' 
                    ? place.geometry.location.lat() 
                    : place.geometry.location.lat)
                : undefined;
              const restaurantLng = place.geometry?.location 
                ? (typeof place.geometry.location.lng === 'function' 
                    ? place.geometry.location.lng() 
                    : place.geometry.location.lng)
                : undefined;
              
              // Get cached timezone offset (from Google Time Zone API)
              const timezoneOffset = timezoneOffsets.get(place.place_id);

              return (
                <article 
                  key={place.place_id} 
                  className={`px-4 py-5 sm:p-6 lg:p-8 border-b last:border-b-0 transition-colors group ${isDark ? 'border-dark-border hover:bg-dark-surface' : 'border-braun-border hover:bg-white'}`}
                >
                  {/* Grid Layout: Fixed number column + content */}
                  <div className="flex gap-4 lg:gap-6">
                    {/* Fixed-width number column */}
                    <div className="flex-shrink-0 w-6 lg:w-8">
                      <span className="font-mono text-braun-orange text-sm lg:text-sm font-bold tabular-nums leading-tight">
                        {(idx + 1).toString().padStart(2, '0')}
                      </span>
                    </div>
                    
                    {/* Content column */}
                    <div className="flex-1 min-w-0 space-y-3 sm:space-y-4">
                      {/* Name row */}
                      <div className="flex flex-wrap items-center gap-2">
                        <a 
                          href={getPlaceUrl(place.name, place.vicinity, place.place_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackRestaurantClicked({
                            placeName: place.name,
                            placeId: place.place_id,
                            rating: place.rating,
                            rank: idx + 1,
                            city: userAddress
                          })}
                          className={`font-sans font-bold text-lg lg:text-xl leading-tight group-hover:text-braun-orange transition-colors ${isDark ? 'text-dark-text' : 'text-braun-dark'}`}
                        >
                          {place.name}
                        </a>
                        
                        {/* NEW OPENING BADGE */}
                        {place.is_new_opening && (
                          <span className={`font-mono text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-[1px] animate-pulse border ${isDark ? 'text-blue-400 border-blue-400/50 bg-blue-400/10' : 'text-blue-600 border-blue-600/50 bg-blue-50'}`}>
                            FRESH DROP
                          </span>
                        )}
                      </div>

                      {/* Metadata row - 2x2 grid on mobile, flex row on desktop */}
                      <div className={`font-mono text-[10px] lg:text-[11px] uppercase tracking-wider ${isDark ? 'text-[#888]' : 'text-braun-text-muted'}`}>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:flex sm:flex-wrap sm:gap-x-6 lg:gap-x-8 sm:gap-y-1">
                          <span className="inline-flex items-center gap-2">
                            <span className={`${isDark ? 'text-dark-text' : 'text-braun-dark'} font-bold`}>RATING:</span>
                            <span>{(place.rating || 0).toFixed(1)}</span>
                          </span>
                          <span className="inline-flex items-center gap-2">
                            <span className={`${isDark ? 'text-dark-text' : 'text-braun-dark'} font-bold`}>COST:</span>
                            <span>{place.price_level && place.price_level > 0 
                              ? '$'.repeat(place.price_level) 
                              : (place.price_level === 0 ? 'Free' : '$$')}</span>
                          </span>
                          <span className="inline-flex items-center gap-2">
                            <span className={`${isDark ? 'text-dark-text' : 'text-braun-dark'} font-bold`}>{travelModeLabel}:</span>
                            <span>{place.walking_time_text || 'N/A'}</span>
                          </span>
                          <span className="inline-flex items-center gap-2">
                            <span className={`${isDark ? 'text-dark-text' : 'text-braun-dark'} font-bold`}>HOURS:</span>
                            {place.opening_hours ? (
                              (() => {
                                // Pass restaurant's coordinates and timezone offset for accurate status
                                const status = getOpenStatus(todaysHours, restaurantLat, restaurantLng, timezoneOffset);
                                if (status.isOpen === true) {
                                  return <span className="text-green-500 font-bold">OPEN</span>;
                                } else if (status.isOpen === false) {
                                  // Show next opening time if available
                                  if (status.nextOpenTime) {
                                    return <span className="text-red-500 font-bold">OPENS {status.nextOpenTime}</span>;
                                  }
                                  // Distinguish between "never opens today" vs "already closed for the day"
                                  if (isClosedAllDay(todaysHours)) {
                                    return <span className="text-red-500 font-bold">CLOSED TODAY</span>;
                                  }
                                  // Had hours today but now closed - just say CLOSED
                                  return <span className="text-red-500 font-bold">CLOSED</span>;
                                }
                                // Fallback to API's open_now status
                                return place.opening_hours?.open_now 
                                  ? <span className="text-green-500 font-bold">OPEN</span>
                                  : <span className="text-red-500 font-bold">CLOSED</span>;
                              })()
                            ) : <span>N/A</span>}
                          </span>
                        </div>
                      </div>

                      {/* Description - use available width on desktop */}
                      <p className={`font-sans text-[13px] lg:text-sm leading-relaxed max-w-xl lg:max-w-none ${isDark ? 'text-[#AAA]' : 'text-[#444]'}`}>
                        {place.ai_reason}
                      </p>

                      {/* TRY section */}
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2 sm:pt-3">
                        <div className="flex items-center gap-2">
                          <span className={`font-mono text-[10px] lg:text-[11px] font-bold uppercase tracking-wider ${isDark ? 'text-[#666]' : 'text-braun-text-muted'}`}>TRY:</span>
                          <span className={`font-sans text-[13px] lg:text-sm font-medium border-b ${isDark ? 'text-dark-text border-dark-border' : 'text-braun-dark border-braun-dark/20'}`}>
                            {place.recommended_dish}
                          </span>
                        </div>

                        {place.is_cash_only && (
                          <span className="font-mono text-[9px] lg:text-[10px] font-bold text-braun-orange uppercase tracking-widest border border-braun-orange/30 px-2 py-0.5 rounded-[1px]">
                            <span className="hidden sm:inline">LEGACY PAYMENT METHOD DETECTED</span>
                            <span className="sm:hidden">CASH ONLY</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Map Column - hidden on mobile, shown on lg+ screens */}
          <div className={`hidden lg:block lg:w-[420px] lg:border-l relative flex-shrink-0 ${isDark ? 'border-dark-border bg-[#181818]' : 'border-braun-border bg-[#E5E5E0]'}`}>
            <MapComponent
              userLat={userLat}
              userLng={userLng}
              results={results}
              theme={theme}
            />
          </div>

        </div>
      </div>
    </div>
  );
};

export default ResultsView;
